import { ComponentType, ReactNode } from "react";

import { DispatchDrawers, drawersActions } from "contexts/drawers";
import { DispatchSidePanels, sidePanelsActions } from "contexts/sidePanels";
import { WindowConfig, windowsActions } from "contexts/windows";
import { DispatchWindows } from "contexts/windows/reducer";

import { WindowPosition } from "./windowPositioning";

/**
 * Unified registration for internal windows that should appear in all three
 * rendering contexts: floating window, side panel, and drawer.
 *
 * The component passed here MUST use `SlottedWindow` internally so it adapts
 * its chrome to the current platform context automatically.
 */
export interface InternalWindowRegistration {
    id: string;
    title: string;
    icon?: ReactNode;
    component: ComponentType<{ onClose?: () => void }>;
    defaultPosition?: WindowPosition;
    dockable?: boolean;
}

interface Dispatchers {
    windows: DispatchWindows;
    panels: DispatchSidePanels;
    drawers: DispatchDrawers;
}

export function registerInternalWindow(reg: InternalWindowRegistration, dispatchers: Dispatchers) {
    const { id, title, icon, component, defaultPosition = "center", dockable } = reg;

    const windowConfig: WindowConfig = {
        id,
        defaultPosition,
        component: component as ComponentType<{ position?: WindowPosition; onClose?: () => void }>,
        ...(dockable !== undefined && { dockable }),
    };
    dispatchers.windows(windowsActions.addWindow(windowConfig));

    dispatchers.panels(
        sidePanelsActions.registerPanel({
            id,
            title,
            icon,
            component: component as ComponentType<{ onClose: () => void }>,
            hideHeader: true,
        }),
    );

    dispatchers.drawers(
        drawersActions.registerDrawer({
            id,
            title,
            icon,
            component: component as ComponentType<{ onClose: () => void }>,
            hideHeader: true,
        }),
    );
}
